/**
 * Database helper with automatic schema selection
 * Use this instead of supabase.from() for direct schema access
 */

import { supabase } from './client';
import { getSchemaForTable, SchemaName } from './schema';

/**
 * Query a table with automatic schema selection
 * This replaces supabase.from() calls with proper schema routing
 * 
 * @example
 * // Instead of: supabase.from('kunden')
 * // Use: fromTable('kunden')
 * 
 * const { data } = await fromTable('kunden').select('*');
 */
export function fromTable(tableName: string) {
  const schema = getSchemaForTable(tableName);
  
  if (schema === 'public') {
    // Use default public schema
    return supabase.from(tableName as any);
  }
  
  // Use schema-specific client for non-public schemas
  // @ts-ignore - Supabase types don't include custom schemas but they work at runtime
  return supabase.schema(schema).from(tableName);
}

/**
 * Query a table from a specific schema (explicit)
 * Use when you need to override automatic schema detection
 * 
 * @example
 * const { data } = await fromSchema('crm', 'kunden').select('*');
 */
export function fromSchema(schema: SchemaName, tableName: string) {
  if (schema === 'public') {
    return supabase.from(tableName as any);
  }
  
  // @ts-ignore - Supabase types don't include custom schemas but they work at runtime
  return supabase.schema(schema).from(tableName);
}

/**
 * Export supabase client for RPC calls and other operations
 * RPC functions remain in public schema
 */
export { supabase };
